 

import React, { useState } from 'react';
import {
    Box, Typography, Grid, TextField, Button,
    FormControl, InputLabel, Select, MenuItem,
    Paper, Divider
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';

const ProcuringHearingAid = () => {
    const [opIpNumber, setOpIpNumber] = useState('');
    const [date, setDate] = useState('');
    const [showDemographicForm, setShowDemographicForm] = useState(false);
    
    // Demographic Data State
    const [demographicData, setDemographicData] = useState({
        name: "",
        age: "",
        gender: "",
        date: "",
        contact: "",
        address: "",
        hearingLossType: "",
        audiogramDate: ""
    });

    // Hearing Aid State
    const [hearingAidData, setHearingAidData] = useState({
        // Programs
        selectedProgramEar: 'right',
        rightHA: { p1: '', p2: '', p3: '' },
        leftHA: { p1: '', p2: '', p3: '' },
        
        // Features
        features: {
            selectedEar: 'right',
            right: {
                directionMicrophone: '',
                feedback: '',
                dnr: '',
                vc: '',
                dome: '',
                size: ''
            },
            left: {
                directionMicrophone: '',
                feedback: '',
                dnr: '',
                vc: '',
                dome: '',
                size: ''
            }
        },
        
        // Ear Mould
        earMould: {
            selectedEar: 'right',
            right: {
                type: '',
                material: ''
            },
            left: {
                type: '',
                material: ''
            }
        },
        
        // Track saved configurations
        savedFeatures: {
            right: false,
            left: false
        },
        savedEarMould: {
            right: false,
            left: false
        }
    });

    const handleOpIpSubmit = () => {
        setShowDemographicForm(true);
    };

    const handleDemographicChange = (field, value) => {
        setDemographicData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleProgramChange = (ear, program, value) => {
        setHearingAidData(prev => ({
            ...prev,
            [ear]: {
                ...prev[ear],
                [program]: value
            }
        }));
    };

    const handleFeatureChange = (ear, field, value) => {
        setHearingAidData(prev => ({
            ...prev,
            features: {
                ...prev.features,
                [ear]: {
                    ...prev.features[ear],
                    [field]: value
                }
            }
        }));
    };

    const handleEarMouldChange = (ear, field, value) => {
        setHearingAidData(prev => ({
            ...prev,
            earMould: {
                ...prev.earMould,
                [ear]: {
                    ...prev.earMould[ear],
                    [field]: value
                }
            }
        }));
    };

    const saveFeatures = (ear) => {
        setHearingAidData(prev => ({
            ...prev,
            savedFeatures: {
                ...prev.savedFeatures,
                [ear]: true
            }
        }));
        alert(`Features for ${ear === 'right' ? 'Right' : 'Left'} ear saved!`);
    };

    const saveEarMould = (ear) => {
        setHearingAidData(prev => ({
            ...prev,
            savedEarMould: {
                ...prev.savedEarMould,
                [ear]: true
            }
        }));
        alert(`Ear mould for ${ear === 'right' ? 'Right' : 'Left'} ear saved!`);
    };

    const handleSubmit = () => {
        // Create final data object
        const formData = {
            opIpNumber,
            demographicData,
            hearingAidData
        };
        
        console.log("Form Data:", formData);
        alert("Hearing aid data submitted successfully!");
        
        // Reset form
        setOpIpNumber('');
        setShowDemographicForm(false);
        setDemographicData({
            name: "",
            age: "",
            gender: "",
            date: "",
            contact: "",
            address: "",
            hearingLossType: "",
            audiogramDate: ""
        });
        setHearingAidData({
            selectedProgramEar: 'right',
            rightHA: { p1: '', p2: '', p3: '' },
            leftHA: { p1: '', p2: '', p3: '' },
            features: {
                selectedEar: 'right',
                right: {
                    directionMicrophone: '',
                    feedback: '',
                    dnr: '',
                    vc: '',
                    dome: '',
                    size: ''
                },
                left: {
                    directionMicrophone: '',
                    feedback: '',
                    dnr: '',
                    vc: '',
                    dome: '',
                    size: ''
                }
            },
            earMould: {
                selectedEar: 'right',
                right: {
                    type: '',
                    material: ''
                },
                left: {
                    type: '',
                    material: ''
                }
            },
            savedFeatures: {
                right: false,
                left: false
            },
            savedEarMould: {
                right: false,
                left: false
            }
        });
    };

    return (
        <Paper elevation={3} sx={{ p: 4, maxWidth: 1400, margin: 'auto', mt: 4, mb: 6 }}>
            <Box sx={{ maxWidth: 1200, margin: '0 auto', p: 3 }}>
                <Typography variant="h4" gutterBottom fontWeight="bold" color="secondary" align="center">
                    Hearing Aid Programming
                </Typography>
                
                {/* OP/IP Number Section */}
                <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
                    paitent Number
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="IP/OP/Internal No"
                                value={opIpNumber}
                                onChange={(e) => setOpIpNumber(e.target.value)}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleOpIpSubmit}
                                disabled={!opIpNumber}
                                sx={{ height: '56px' }}
                            >
                                Submit Number
                            </Button>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Demographic Form */}
                {showDemographicForm && (
                    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                        <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
                            Demographic Details
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Name"
                                    value={demographicData.name}
                                    onChange={(e) => handleDemographicChange('name', e.target.value)}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Age"
                                    value={demographicData.age}
                                    onChange={(e) => handleDemographicChange('age', e.target.value)}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Gender"
                                    value={demographicData.gender}
                                    onChange={(e) => handleDemographicChange('gender', e.target.value)}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label="Date"
                                    type="date"
                                    value={demographicData.date}
                                    onChange={(e) => handleDemographicChange('date', e.target.value)}
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Contact"
                                    value={demographicData.contact}
                                    onChange={(e) => handleDemographicChange('contact', e.target.value)}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Hearing Loss Type"
                                    value={demographicData.hearingLossType}
                                    onChange={(e) => handleDemographicChange('hearingLossType', e.target.value)}
                                    variant="outlined"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Audiogram Date"
                                    type="date"
                                    value={demographicData.audiogramDate}
                                    onChange={(e) => handleDemographicChange('audiogramDate', e.target.value)}
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Address"
                                    value={demographicData.address}
                                    onChange={(e) => handleDemographicChange('address', e.target.value)}
                                    variant="outlined"
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                )}

                {/* Hearing Aid Configuration */}
                {showDemographicForm && (
                    <>
                        {/* Programs Section */}
                        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                              <Box mb={4} border={1} borderColor="divider" p={2} borderRadius={2}>
                                                            <Grid item xs={12} md={6}>
                                                              <TextField
                                                                fullWidth
                                                                name="dateOfAssessment"
                                                                label="Date of Assessment"
                                                                type="date"
                                                                value={date}
                                                                onChange={(e) => setDate(e.target.value)}
                                                                InputLabelProps={{ shrink: true }}
                                                              />
                                                            </Grid>
                                                      </Box>
                            <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
                                Hearing Aid Programs
                            </Typography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth sx={{ mb: 3 }}>
                                        <InputLabel shrink={true}>Select Hearing Aid</InputLabel>
                                        <Select
                                            value={hearingAidData.selectedProgramEar}
                                            onChange={(e) => setHearingAidData(prev => ({
                                                ...prev,
                                                selectedProgramEar: e.target.value
                                            }))}
                                            label="Select Hearing Aid"
                                            displayEmpty
                                            renderValue={value => value || "Select Hearing Aid"}
                                        >
                                            <MenuItem value="right">Right HA</MenuItem>
                                            <MenuItem value="left">Left HA</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                
                                {hearingAidData.selectedProgramEar === 'right' && (
                                    <>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="P1"
                                                value={hearingAidData.rightHA.p1}
                                                onChange={(e) => handleProgramChange('rightHA', 'p1', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="P2"
                                                value={hearingAidData.rightHA.p2}
                                                onChange={(e) => handleProgramChange('rightHA', 'p2', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="P3"
                                                value={hearingAidData.rightHA.p3}
                                                onChange={(e) => handleProgramChange('rightHA', 'p3', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                    </>
                                )}
                                
                                {hearingAidData.selectedProgramEar === 'left' && (
                                    <>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="P1"
                                                value={hearingAidData.leftHA.p1}
                                                onChange={(e) => handleProgramChange('leftHA', 'p1', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="P2"
                                                value={hearingAidData.leftHA.p2}
                                                onChange={(e) => handleProgramChange('leftHA', 'p2', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField
                                                fullWidth
                                                label="P3"
                                                value={hearingAidData.leftHA.p3}
                                                onChange={(e) => handleProgramChange('leftHA', 'p3', e.target.value)}
                                                variant="outlined"
                                            />
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Paper>

                        {/* Features Section */}
                        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                            <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
                                Features Configuration
                            </Typography>
                            
                            {/* Ear Selection for Features */}
                            <Grid item xs={12} sx={{ mb: 3 }}>
                                <FormControl fullWidth>
                                    <InputLabel shrink={true}>Select Ear</InputLabel>
                                    <Select
                                        value={hearingAidData.features.selectedEar}
                                        onChange={(e) => setHearingAidData(prev => ({
                                            ...prev,
                                            features: {
                                                ...prev.features,
                                                selectedEar: e.target.value
                                            }
                                        }))}
                                        label="Select Ear"
                                        displayEmpty
                                        renderValue={value => value || "Select Ear"}
                                    >
                                        <MenuItem value="right">Right Ear</MenuItem>
                                        <MenuItem value="left">Left Ear</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink={true}>Direction Microphone</InputLabel>
                                        <Select
                                            value={
                                                hearingAidData.features.selectedEar === 'right' 
                                                ? hearingAidData.features.right.directionMicrophone 
                                                : hearingAidData.features.left.directionMicrophone
                                            }
                                            onChange={(e) => handleFeatureChange(
                                                hearingAidData.features.selectedEar,
                                                'directionMicrophone',
                                                e.target.value
                                            )}
                                            label="Direction Microphone"
                                            displayEmpty
                                            renderValue={value => value || "Select Direction Microphone"}
                                        >
                                            <MenuItem value="Activated">Activated</MenuItem>
                                            <MenuItem value="Inactivated">Inactivated</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink={true}>Feedback</InputLabel>
                                        <Select
                                            value={
                                                hearingAidData.features.selectedEar === 'right' 
                                                ? hearingAidData.features.right.feedback 
                                                : hearingAidData.features.left.feedback
                                            }
                                            onChange={(e) => handleFeatureChange(
                                                hearingAidData.features.selectedEar,
                                                'feedback',
                                                e.target.value
                                            )}
                                            label="Feedback"
                                            displayEmpty
                                            renderValue={value => value || "Select Feedback"}
                                        >
                                            <MenuItem value="Activated">Activated</MenuItem>
                                            <MenuItem value="Inactivated">Inactivated</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink={true}>DNR</InputLabel>
                                        <Select
                                            value={
                                                hearingAidData.features.selectedEar === 'right' 
                                                ? hearingAidData.features.right.dnr 
                                                : hearingAidData.features.left.dnr
                                            }
                                            onChange={(e) => handleFeatureChange(
                                                hearingAidData.features.selectedEar,
                                                'dnr',
                                                e.target.value
                                            )}
                                            label="DNR"
                                            displayEmpty
                                            renderValue={value => value || "Select DNR"}
                                        >
                                            <MenuItem value="Activated">Activated</MenuItem>
                                            <MenuItem value="Inactivated">Inactivated</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink={true}>VC</InputLabel>
                                        <Select
                                            value={
                                                hearingAidData.features.selectedEar === 'right' 
                                                ? hearingAidData.features.right.vc 
                                                : hearingAidData.features.left.vc
                                            }
                                            onChange={(e) => handleFeatureChange(
                                                hearingAidData.features.selectedEar,
                                                'vc',
                                                e.target.value
                                            )}
                                            label="VC"
                                            displayEmpty
                                            renderValue={value => value || "Select VC"}
                                        >
                                            <MenuItem value="Enabled">Enabled</MenuItem>
                                            <MenuItem value="Disabled">Disabled</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink={true}>Dome</InputLabel>
                                        <Select
                                            value={
                                                hearingAidData.features.selectedEar === 'right' 
                                                ? hearingAidData.features.right.dome 
                                                : hearingAidData.features.left.dome
                                            }
                                            onChange={(e) => handleFeatureChange(
                                                hearingAidData.features.selectedEar,
                                                'dome',
                                                e.target.value
                                            )}
                                            label="Dome"
                                            displayEmpty
                                            renderValue={value => value || "Select Dome"}
                                        >
                                            <MenuItem value="None">None</MenuItem>
                                            <MenuItem value="Power">Power</MenuItem>
                                            <MenuItem value="Double">Double</MenuItem>
                                            <MenuItem value="Tulip">Tulip</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink={true}>Size</InputLabel>
                                        <Select
                                            value={
                                                hearingAidData.features.selectedEar === 'right' 
                                                ? hearingAidData.features.right.size 
                                                : hearingAidData.features.left.size
                                            }
                                            onChange={(e) => handleFeatureChange(
                                                hearingAidData.features.selectedEar,
                                                'size',
                                                e.target.value
                                            )}
                                            label="Size"
                                            displayEmpty
                                            renderValue={value => value || "Select Size"}
                                        >
                                            <MenuItem value="None">None</MenuItem>
                                            <MenuItem value="2L">2L</MenuItem>
                                            <MenuItem value="3L">3L</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            
                            {/* Save Features Button */}
                            <Box sx={{ textAlign: 'center', mt: 3 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => saveFeatures(hearingAidData.features.selectedEar)}
                                    sx={{ mt: 2, px: 4, py: 1.5, fontSize: '1.1rem' }}
                                >
                                    Save Features for {hearingAidData.features.selectedEar === 'right' ? 'Right' : 'Left'} Ear
                                </Button>
                            </Box>
                        </Paper>

                        {/* Ear Mould Section */}
                        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                            <Typography variant="h5" gutterBottom fontWeight="bold" color="secondary">
                                Ear Mould Configuration
                            </Typography>
                            
                            {/* Ear Selection for Ear Mould */}
                            <Grid item xs={12} sx={{ mb: 3 }}>
                                <FormControl fullWidth>
                                    <InputLabel shrink={true}>Select Ear</InputLabel>
                                    <Select
                                        value={hearingAidData.earMould.selectedEar}
                                        onChange={(e) => setHearingAidData(prev => ({
                                            ...prev,
                                            earMould: {
                                                ...prev.earMould,
                                                selectedEar: e.target.value
                                            }
                                        }))}
                                        label="Select Ear"
                                        displayEmpty
                                        renderValue={value => value || "Select Ear"}
                                    >
                                        <MenuItem value="right">Right Ear</MenuItem>
                                        <MenuItem value="left">Left Ear</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink={true}>Type</InputLabel>
                                        <Select
                                            value={
                                                hearingAidData.earMould.selectedEar === 'right' 
                                                ? hearingAidData.earMould.right.type 
                                                : hearingAidData.earMould.left.type
                                            }
                                            onChange={(e) => handleEarMouldChange(
                                                hearingAidData.earMould.selectedEar,
                                                'type',
                                                e.target.value
                                            )}
                                            label="Type"
                                            displayEmpty
                                            renderValue={value => value || "Select Type"}
                                        >
                                            <MenuItem value="Regular">Regular</MenuItem>
                                            <MenuItem value="Shell">Shell</MenuItem>
                                            <MenuItem value="Canal">Canal</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <FormControl fullWidth>
                                        <InputLabel shrink={true}>Material</InputLabel>
                                        <Select
                                            value={
                                                hearingAidData.earMould.selectedEar === 'right' 
                                                ? hearingAidData.earMould.right.material 
                                                : hearingAidData.earMould.left.material
                                            }
                                            onChange={(e) => handleEarMouldChange(
                                                hearingAidData.earMould.selectedEar,
                                                'material',
                                                e.target.value
                                            )}
                                            label="Material"
                                            displayEmpty
                                            renderValue={value => value || "Select Material"}
                                        >
                                            <MenuItem value="Soft">Soft</MenuItem>
                                            <MenuItem value="Hard">Hard</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            
                            {/* Save Ear Mould Button */}
                            <Box sx={{ textAlign: 'center', mt: 3 }}>
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => saveEarMould(hearingAidData.earMould.selectedEar)}
                                    sx={{ mt: 2, px: 4, py: 1.5, fontSize: '1.1rem' }}
                                >
                                    Save Ear Mould for {hearingAidData.earMould.selectedEar === 'right' ? 'Right' : 'Left'} Ear
                                </Button>
                            </Box>
                        </Paper>

                        {/* Final Submit Button */}
                        <Box sx={{ textAlign: 'center', mt: 4 }}>
                            <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                                {hearingAidData.savedFeatures.right && "✓ Right features saved "}
                                {hearingAidData.savedFeatures.left && "✓ Left features saved "}
                                {hearingAidData.savedEarMould.right && "✓ Right ear mould saved "}
                                {hearingAidData.savedEarMould.left && "✓ Left ear mould saved "}
                            </Typography>
                            
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSubmit}
                                sx={{ mt: 2, px: 4, py: 1.5, fontSize: '1.1rem' }}
                            >
                                Submit All Configurations
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
        </Paper>
    );
};

export default ProcuringHearingAid;